import { emptyApi } from "store/apis/emptyApi";
import { groupApi } from "store/apis/group";

export type SeasonAnswers = Record<
  string,
  string | number | boolean | string[] | null
>;

export type HomepagePayload = Record<string, SeasonAnswers>;

type HomepageScope = { groupId: string; entityId: string };

export type TaxYearsEntity = {
  uuid: string;
  name: string;
  default_tax_year: string;
  past_tax_years: string[];
  tracked_years: string[];
};

export type TaxYearsData = {
  current_tax_year: string;
  entities: TaxYearsEntity[];
};

export type TrackedYearsUpdate = { uuid: string; tracked_years: string[] };

export type EntityCartAmount = {
  uuid: string;
  name: string;
  amount: number;
  items_count: number;
};

export type TaxFilingMarkedPaid = {
  is_tax_filing_marked_paid: boolean;
  tax_filing_marked_paid_by: string;
};

export type EntitiesCartTotal = {
  total_amount: number;
  total_items_count: number;
  entities: EntityCartAmount[];
};

export type DetectedFilingState = {
  name: string;
  country: string;
  state_id: string;
};

export type DetectedFilingProduct = {
  product_id: string;
  product_content_type_id: number;
  product_name: string;
  product_description: string;
  base_task_key: string;
  has_balancing_payment: boolean;
  state: DetectedFilingState | null;
};

export type DetectedFilingCartItem = {
  uuid: string;
  quantity: number;
  total: number;
  after_discount_price: number;
  status: string;
  type: string;
  product_details: DetectedFilingProduct;
  alarm_id: string | null;

  group: string | null;
  description: string | null;
  deadline: string | null;
  is_overdue: boolean;
};

export type DetectedFilingCartItemsBySeason = Record<
  string,
  DetectedFilingCartItem[]
>;

export type DetectedFilingsResponse = {
  uuid: string;
  group_id: string;
  entity_id: string;
  entity_name: string;
  cart_items: DetectedFilingCartItemsBySeason;
  order_summary: {
    subtotal: number;
    items_count: number;
    discount: number;
    total: number;
  };
  total_subscription_credit_amount: number;
};

export type DetectedFilingsCountEntity = {
  uuid: string;
  name: string;
  detected_filings_count: number;
};

export type DetectedFilingsCountResponse = {
  entities: DetectedFilingsCountEntity[];
};

const homepageUrl = ({ groupId, entityId }: HomepageScope): string =>
  `/api/inkle/tax_compliance/group/${groupId}/entity/${entityId}/homepage/`;

export const taxSetupApi = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getTaxYears: build.query<TaxYearsData, { groupId: string }>({
      query: ({ groupId }) =>
        `/api/inkle/tax_compliance/group/${groupId}/tax_years/`,
      transformResponse: (
        response: TaxYearsData | { data: TaxYearsData }
      ): TaxYearsData => ("data" in response ? response.data : response),
      providesTags: ["FILINGS_DASHBOARD", "TAX_DETECTED_FILINGS"],
    }),

    addTaxYears: build.mutation<
      void,
      { groupId: string; entities: TrackedYearsUpdate[] }
    >({
      query: ({ groupId, entities }) => ({
        url: `/api/inkle/tax_compliance/group/${groupId}/tax_years/`,
        method: "POST",
        body: { entities },
      }),
      invalidatesTags: ["FILINGS_DASHBOARD"],
    }),

    getEntitiesCartTotal: build.query<EntitiesCartTotal, { groupId: string }>({
      query: ({ groupId }) =>
        `/api/inkle/tax_compliance/group/${groupId}/entities_cart_total/`,
      transformResponse: (
        response: EntitiesCartTotal | { data: EntitiesCartTotal }
      ): EntitiesCartTotal => ("data" in response ? response.data : response),
      providesTags: [
        "FILINGS_DASHBOARD",
        "TAX_ENTITIES_CART_TOTAL",
        "TAX_DETECTED_FILINGS",
        "CART",
      ],
    }),

    getDetectedFilings: build.query<DetectedFilingsResponse, HomepageScope>({
      query: ({ groupId, entityId }) =>
        `/api/inkle/tax_compliance/group/${groupId}/entity/${entityId}/detected_filings/`,
      transformResponse: (
        response: DetectedFilingsResponse | { data: DetectedFilingsResponse }
      ): DetectedFilingsResponse =>
        "data" in response ? response.data : response,
      providesTags: ["TAX_DETECTED_FILINGS"],
    }),

    getDetectedFilingsCount: build.query<
      DetectedFilingsCountResponse,
      { groupId: string }
    >({
      query: ({ groupId }) =>
        `/api/inkle/tax_compliance/group/${groupId}/detected_filings_count/`,
      transformResponse: (
        response:
          | DetectedFilingsCountResponse
          | { data: DetectedFilingsCountResponse }
      ): DetectedFilingsCountResponse =>
        "data" in response ? response.data : response,
      providesTags: ["TAX_DETECTED_FILINGS"],
    }),

    getHomepage: build.query<
      HomepagePayload,
      HomepageScope & { seasons: number[] }
    >({
      query: ({ seasons, ...scope }) => ({
        url: homepageUrl(scope),
        params: { seasons: seasons.join(",") },
      }),
      providesTags: ["TAX_SETUP_ANSWERS"],
    }),

    putHomepage: build.mutation<
      HomepagePayload,
      HomepageScope & { payload: HomepagePayload }
    >({
      query: ({ payload, ...scope }) => ({
        url: homepageUrl(scope),
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: [
        "TAX_SETUP_ANSWERS",
        "TAX_DETECTED_FILINGS",
        "FILINGS_DASHBOARD",
      ],
    }),

    patchHomepage: build.mutation<
      HomepagePayload,
      HomepageScope & { payload: HomepagePayload; seasons: number[] }
    >({
      query: ({ payload, seasons, ...scope }) => ({
        url: homepageUrl(scope),
        method: "PATCH",
        body: payload,
      }),

      onQueryStarted: async (
        { groupId, entityId, seasons, payload },
        { dispatch, queryFulfilled }
      ) => {
        const result = await queryFulfilled.catch(() => null);
        if (!result) return;

        dispatch(
          taxSetupApi.util.updateQueryData(
            "getHomepage",
            { groupId, entityId, seasons },
            (draft) => {
              Object.entries(payload).forEach(([season, answers]) => {
                draft[season] = { ...(draft[season] ?? {}), ...answers };
              });
            }
          )
        );
      },

      invalidatesTags: ["TAX_DETECTED_FILINGS", "TAX_ENTITIES_CART_TOTAL"],
    }),

    upload7004FiledCopy: build.mutation<
      void,
      HomepageScope & { season: number; file: File }
    >({
      query: ({ season, file, ...scope }) => {
        const body = new FormData();
        body.append("file", file);

        return {
          url: `${homepageUrl(scope)}7004_filed_copy/${season}/`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["TAX_SETUP_ANSWERS"],
    }),

    deleteTaxSeason: build.mutation<void, HomepageScope & { season: number }>({
      query: ({ season, ...scope }) => ({
        url: `${homepageUrl(scope)}${season}/`,
        method: "DELETE",
      }),
      invalidatesTags: [
        "TAX_SETUP_ANSWERS",
        "TAX_DETECTED_FILINGS",
        "FILINGS_DASHBOARD",
      ],
    }),

    deleteTaxFile: build.mutation<void, HomepageScope & { season: number }>({
      query: ({ season, ...scope }) => ({
        url: `${homepageUrl(scope)}7004_filed_copy/${season}/`,
        method: "DELETE",
      }),
      invalidatesTags: [
        "TAX_SETUP_ANSWERS",
        "TAX_DETECTED_FILINGS",
        "FILINGS_DASHBOARD",
      ],
    }),

    markTaxFilingPaid: build.mutation<TaxFilingMarkedPaid, { groupId: string }>(
      {
        query: ({ groupId }) => ({
          url: `/api/inkle/company/group/${groupId}/tax-filing/mark-paid/`,
          method: "POST",
        }),
        transformResponse: (response: { data: TaxFilingMarkedPaid }) =>
          response.data,

        onQueryStarted: async (_args, { dispatch, queryFulfilled }) => {
          const result = await queryFulfilled.catch(() => null);
          if (!result) return;

          dispatch(
            groupApi.util.updateQueryData("getEntities", undefined, (draft) => {
              draft.groups.forEach((group) => {
                group.has_paid_for_tax_filing = true;
              });
            })
          );
        },
      }
    ),
  }),
});

export const {
  useGetTaxYearsQuery,
  useAddTaxYearsMutation,
  useGetEntitiesCartTotalQuery,
  useGetDetectedFilingsQuery,
  useGetDetectedFilingsCountQuery,
  useGetHomepageQuery,
  usePutHomepageMutation,
  usePatchHomepageMutation,
  useUpload7004FiledCopyMutation,
  useDeleteTaxSeasonMutation,
  useDeleteTaxFileMutation,
  useMarkTaxFilingPaidMutation,
} = taxSetupApi;
